import React from 'react';
import { User, Globe, Clock, FolderKanban, SlidersHorizontal, Copy, Check, RefreshCw, Power, Settings } from 'lucide-react';
import type { CollaboratorPortalAccess, CollaboratorPortalProject } from '../../../lib/services/collaboratorAuthService';
import { PropRow } from '../../shared/activity-drawer/ui/PropRow';
import { LANGUAGE_LABELS, permissionsSummary, formatLastAccess } from './collaboratorAccessHelpers';
import { GhostAction } from './GhostAction';
import { CredentialsCallout } from './CredentialsCallout';

export interface ProjectsPortalAccessDetailsProps {
  access: CollaboratorPortalAccess;
  projects: CollaboratorPortalProject[];
  sessionPassword: string | null;
  copiedField: 'credentials' | 'username' | null;
  busy: boolean;
  onCopyUsername(): void;
  onCopyCredentials(): void;
  onCopySessionPassword(): void;
  onNewPassword(): void;
  onToggleActive(): void;
  onEdit(): void;
}

/**
 * Contenuto della sezione "Portale progetti" quando esiste un accesso: callout
 * credenziali (se appena generate), proprietà (username, lingua, ultimo accesso,
 * progetti, permessi) e azioni fantasma. Solo presentazione: la logica resta nel
 * container `PartnerProjectsPortalSection`.
 */
export function ProjectsPortalAccessDetails({
  access,
  projects,
  sessionPassword,
  copiedField,
  busy,
  onCopyUsername,
  onCopyCredentials,
  onCopySessionPassword,
  onNewPassword,
  onToggleActive,
  onEdit
}: ProjectsPortalAccessDetailsProps): JSX.Element {
  return (
    <>
      {sessionPassword && <CredentialsCallout password={sessionPassword} onCopy={onCopySessionPassword} />}

      {!access.is_active && (
        <div className="px-1.5 text-[11px] text-[rgba(55,53,47,0.45)] dark:text-white/40">
          L'accesso è sospeso: il collaboratore non può entrare nel portale.
        </div>
      )}

      <div className="flex flex-col">
        <PropRow
          icon={<User className="h-3 w-3" strokeWidth={1.5} />}
          label="Username"
          trailing={copiedField === 'username' ? <Check className="h-3 w-3" strokeWidth={1.5} /> : <Copy className="h-3 w-3" strokeWidth={1.5} />}
          onClick={onCopyUsername}
        >
          <span className="font-mono text-[12px]">{access.username}</span>
        </PropRow>
        <PropRow icon={<Globe className="h-3 w-3" strokeWidth={1.5} />} label="Lingua">
          {LANGUAGE_LABELS[access.preferred_language] || access.preferred_language}
        </PropRow>
        <PropRow icon={<Clock className="h-3 w-3" strokeWidth={1.5} />} label="Ultimo accesso">
          <span className={access.last_login_at ? '' : 'text-[rgba(55,53,47,0.45)] dark:text-white/40'}>
            {formatLastAccess(access.last_login_at)}
          </span>
        </PropRow>
        <PropRow icon={<FolderKanban className="h-3 w-3" strokeWidth={1.5} />} label="Progetti" wrap>
          {projects.length === 0 ? (
            <span className="text-[rgba(55,53,47,0.45)] dark:text-white/40">Nessun progetto assegnato</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {projects.slice(0, 3).map((p) => (
                <span
                  key={p.id}
                  className="rounded-full bg-[rgba(55,53,47,0.05)] dark:bg-white/[0.06] px-2 py-0.5 text-[11px] whitespace-nowrap"
                >
                  {p.project?.name || 'Progetto'}
                </span>
              ))}
              {projects.length > 3 && (
                <span className="text-[11px] text-[#787774] dark:text-gray-400">+{projects.length - 3}</span>
              )}
            </div>
          )}
        </PropRow>
        <PropRow icon={<SlidersHorizontal className="h-3 w-3" strokeWidth={1.5} />} label="Permessi">
          {permissionsSummary(access.permissions)}
        </PropRow>
      </div>

      <div className="flex flex-wrap gap-0.5 pt-0.5">
        <GhostAction
          icon={<Settings className="h-3 w-3" strokeWidth={1.5} />}
          label="Modifica"
          onClick={onEdit}
          disabled={busy}
        />
        <GhostAction
          icon={copiedField === 'credentials' ? <Check className="h-3 w-3" strokeWidth={1.5} /> : <Copy className="h-3 w-3" strokeWidth={1.5} />}
          label={copiedField === 'credentials' ? 'Copiato' : 'Copia credenziali'}
          onClick={onCopyCredentials}
          disabled={busy || !sessionPassword}
          title={sessionPassword ? undefined : 'Genera prima una nuova password'}
        />
        <GhostAction
          icon={<RefreshCw className="h-3 w-3" strokeWidth={1.5} />}
          label="Nuova password"
          onClick={onNewPassword}
          disabled={busy}
        />
        <GhostAction
          icon={<Power className="h-3 w-3" strokeWidth={1.5} />}
          label={access.is_active ? 'Disattiva' : 'Riattiva accesso'}
          onClick={onToggleActive}
          disabled={busy}
          danger={access.is_active}
        />
      </div>
    </>
  );
}
